import { useEffect, useId, useRef, useState } from "react";
import { Command } from "cmdk";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/cn";

export interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  label?: string;
  helperText?: string;
  placeholder?: string;
  /** Shown in the filter field. */
  searchPlaceholder?: string;
  emptyMessage?: string;
  id?: string;
  className?: string;
}

/**
 * A searchable select.
 *
 * Built on `cmdk`, which was already a dependency and provides the filtering, the roving
 * `aria-activedescendant` focus and the listbox semantics. No new package, and no hand-rolled
 * keyboard handling to get subtly wrong.
 *
 * This exists because a native `<select>` holding ~420 IANA timezones is unusable: the browser
 * renders its own unthemed popup that ignores the app's palette entirely, sizes itself to the
 * option list rather than the trigger, and offers no way to search — only scrolling, or the
 * browser's own first-letter jump, which is useless when every entry starts with "A".
 *
 * The panel is positioned relative to the field rather than portalled: the field sits in normal
 * document flow inside a card, so an absolutely-positioned panel is anchored correctly without
 * pulling in a floating-ui/popover dependency to compute collisions.
 */
export function Combobox({
  value,
  onChange,
  options,
  label,
  helperText,
  placeholder = "Select…",
  searchPlaceholder = "Type to filter…",
  emptyMessage = "No matches.",
  id,
  className,
}: ComboboxProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click and on Escape. Escape is handled at the wrapper rather than on the
  // input so it also fires when focus has moved into the list.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus(); // returning focus is what makes Escape usable by keyboard
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label htmlFor={fieldId} className="text-sm font-medium text-text">
          {label}
        </label>
      )}

      <div ref={wrapperRef} className="relative">
        <button
          ref={triggerRef}
          id={fieldId}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "flex h-10 w-full items-center justify-between gap-2 rounded-control border border-border bg-surface",
            "px-3 text-left text-sm text-text",
            "transition-colors duration-fast ease-standard",
            "focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/20",
            open && "border-accent ring-2 ring-accent/20",
          )}
        >
          <span className={cn("truncate", !value && "text-text-muted")}>{value || placeholder}</span>
          <ChevronsUpDown className="size-4 shrink-0 text-text-muted" aria-hidden />
        </button>

        {open && (
          <div
            className={cn(
              // max-h on the list keeps this panel short enough that it does not grow the
              // page the way the calendar did, so absolute anchoring is kept here — it also
              // means the panel tracks the field on scroll with no JS.
              "absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden",
              "rounded-card border border-border bg-surface shadow-lg",
              "animate-content-drop",
            )}
          >
            {/* shouldFilter is cmdk's default and does substring + fuzzy scoring over the item
                values, which is why typing "karach" finds "Asia/Karachi". */}
            <Command loop>
              <div className="border-b border-border px-3">
                <Command.Input
                  autoFocus
                  placeholder={searchPlaceholder}
                  className="h-10 w-full bg-transparent text-sm text-text outline-none placeholder:text-text-muted"
                />
              </div>
              {/* Capped height with its own scroll: the panel can never grow past the viewport
                  the way the native popup did, however many options are passed in. */}
              <Command.List className="scrollbar-slim max-h-64 overflow-y-auto overscroll-contain p-1">
                <Command.Empty className="px-3 py-6 text-center text-sm text-text-muted">
                  {emptyMessage}
                </Command.Empty>
                {options.map((option) => (
                  <Command.Item
                    key={option}
                    value={option}
                    onSelect={(selected) => {
                      onChange(selected);
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                    className={cn(
                      "flex cursor-pointer items-center justify-between gap-2 rounded-control px-2.5 py-2 text-sm",
                      "text-text-muted",
                      "data-[selected=true]:bg-surface-hover data-[selected=true]:text-text",
                    )}
                  >
                    <span className="truncate">{option}</span>
                    {option === value && (
                      <Check className="size-4 shrink-0 text-accent" aria-hidden />
                    )}
                  </Command.Item>
                ))}
              </Command.List>
            </Command>
          </div>
        )}
      </div>

      {helperText && <p className="text-xs text-text-muted">{helperText}</p>}
    </div>
  );
}
